import classNames from 'classnames';
import React, { useState } from 'react';

import { DeleteLeaderboardPresets } from '@/connection/leaderboard';
import { updateProjectSettings } from '@/store/slices/project';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { EventSettings } from '@/types/projects.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import toastTrigger from '@ui/toast/toast-trigger';
import { ICONS } from '@ui/icons/types';
import Icon from '@ui/icons/icon';
import Modal from '@ui/modal/modal';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import { TooltipPortalPosition } from '@ui/tooltip-portal/tooltip-portal-types';
import WaitingIndicator from '@ui/waiting-indicator/waiting-indicator';
import LeaderboardCsvModal from './leaderboard-csv-modal';
import { getToken, getUser } from '@/store/slices/user/selectors';
import { getWorkingProject } from '@/store/slices/project/selectors';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';

interface LeaderboardCsvUploadProps {
  panel?: boolean;
}

const LeaderboardCSVUpload: React.FC<LeaderboardCsvUploadProps> = ({ panel }) => {
  const workingEvent = useAppSelector(getWorkingProject);
  const token = useAppSelector(getToken);
  const user = useAppSelector(getUser);

  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();

  const leaderboardSettings = workingEvent?.settings.leaderboard_settings;

  const openModal = () => {
    setCsvModalOpen(true);
  };

  const closeModal = () => {
    setCsvModalOpen(false);
  };

  const handleSubmit = () => {
    toastTrigger(
      <Text>
        <h6>Your upload is processing</h6>
        <p>We will notify you when your upload has finished processing</p>
      </Text>,
      { type: 'info' }
    );

    setCsvModalOpen(false);
    return;
  };

  const closeDeleteConfirmation = () => {
    setDeleteConfirmation(false);
    setLoading(false);
  };

  const handleDeleteCsv = async () => {
    if (token && user?.active_channel && workingEvent) {
      try {
        setLoading(true);
        // delete all rows for uploaded presets on this event
        await DeleteLeaderboardPresets(user.active_channel, workingEvent?.event);

        // remove csv filename + url from event settings and save updates
        const eventSettings: EventSettings = {
          ...workingEvent.settings,
        };

        if (!eventSettings?.leaderboard_settings) return;

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { upload_url, upload_filename, ...rest } = eventSettings.leaderboard_settings;
        const updatedSettings = { ...eventSettings, leaderboard_settings: rest };

        dispatch(updateProjectSettings({ event_uuid: workingEvent.uuid, settings: updatedSettings }));
      } catch (e) {
        console.error(e);
        toastTrigger(
          <div>
            <h6>Error</h6>
            <p>There was an error deleting the uploaded csv.</p>
          </div>,
          { type: 'error' }
        );
      } finally {
        setLoading(false);
        closeDeleteConfirmation();
      }
    }
  };

  const panelEnabled = panel && leaderboardSettings?.enabled;
  const allowPanelSettings = !panel || panelEnabled;
  const displayEditButton = !!leaderboardSettings?.upload_filename && allowPanelSettings;
  const showAddList = panelEnabled && !leaderboardSettings?.upload_filename;

  return (
    <div className="leaderboard-csv-upload-wrapper">
      {workingEvent ? (
        <div className="leaderboard-csv-upload-container">
          <div className="leaderboard-settings-label">
            <div className="leaderboard-custom-points-title">
              <Text tag="span">CUSTOM POINTS UPLOAD</Text>
              <TooltipPortal
                childrenWrapperClass="xyz"
                contentWrapperClass={classNames('leaderboard-csv-tooltip', { 'is-panel': panel })}
                tooltipContent={
                  <Text maxWidth="500px" color="var(--gray-true-dark-100)">
                    Assign custom points to attendee by email for actions not captured automatically listed above. All
                    points are synced to the most recent version of the document. Uploading a new version will overwrite
                    any existing points assigned via CSV.
                  </Text>
                }
                position={panel ? TooltipPortalPosition.BOTTOM : TooltipPortalPosition.TOP}
                keepOpenOnContentHover={false}
              >
                <Icon name={ICONS.PRIMARY_TOOLTIP} color="red" size={12} />
              </TooltipPortal>
            </div>
            <OptionalComponent display={displayEditButton || showAddList}>
              <Button
                clear
                borderRadius="8px"
                padding="0 4px"
                className="flex ai-center"
                onClick={openModal}
                hoverOpacity={0.8}
              >
                <Icon name={ICONS.ADD} size={12} color={getThemeColorCSSVariable()} />
                <Text color={getThemeColorCSSVariable()}>{showAddList ? 'Add' : 'Edit'} List</Text>
              </Button>
            </OptionalComponent>
          </div>
          <div className="leaderboard-small-card">
            {!leaderboardSettings?.upload_filename ? (
              <div className="small-card-container">
                <div className="small-card-info">
                  <div className="small-card-icon">
                    <Icon name={ICONS.DOCUMENTS} size={18} color="var(--gray-400)" />
                  </div>
                  <Text className="small-card-description">
                    <span>Upload Points by CSV</span>
                    <label>There are no custom points</label>
                  </Text>
                </div>
                <OptionalComponent display={!panel}>
                  <Button width="94px" height="32px" onClick={openModal} backgroundColor={getThemeColorCSSVariable()}>
                    <Text
                      color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                    >
                      + Add list
                    </Text>
                  </Button>
                </OptionalComponent>
              </div>
            ) : (
              <>
                <div className="leaderboard-csv-upload-icon">
                  <Icon name={ICONS.VIEWERS_EYE} size={16} color="#FFF" />
                  <span>CSV</span>
                </div>
                <div className="leaderboard-csv-upload-info">
                  <span>{leaderboardSettings.upload_filename}</span>
                </div>
                <OptionalComponent display={!panel}>
                  <div className="leaderboard-remove-csv">
                    <Button
                      className="no-style no-padding"
                      onClick={() => setDeleteConfirmation(true)}
                      focusBackgroundColor="var(--gray-700)"
                    >
                      <Icon name={ICONS.TRASH_OUTLINE} size={24} color="gray" />
                    </Button>
                  </div>
                </OptionalComponent>
              </>
            )}
          </div>
        </div>
      ) : (
        <WaitingIndicator />
      )}
      <LeaderboardCsvModal
        open={csvModalOpen}
        close={closeModal}
        submit={handleSubmit}
        currentSettings={leaderboardSettings}
      />
      <Modal
        open={deleteConfirmation}
        onRequestClose={closeDeleteConfirmation}
        renderHeaderContent={() => (
          <Container padding="8px 16px">
            <Text tag="h4" className="h4">
              Delete Confirmation
            </Text>
          </Container>
        )}
        renderFooterContent={() => (
          <Container padding="8px 16px">
            <div className="flex ai-center jc-flex-end gap-8">
              <Button onClick={closeDeleteConfirmation} focusBackgroundColor="var(--gray-700)">
                <Text>Cancel</Text>
              </Button>
              <Button
                width="94px"
                height="32px"
                onClick={handleDeleteCsv}
                disabled={loading}
                className="flex ai-center jc-center"
                backgroundColor={getThemeColorCSSVariable()}
                loading={loading}
              >
                <Text color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}>
                  Delete
                </Text>
              </Button>
            </div>
          </Container>
        )}
      >
        <Container padding="16px">
          <Text tag="p">
            Are you sure you want to delete the uploaded custom points for this event? This action cannot be undone.
          </Text>
        </Container>
      </Modal>
    </div>
  );
};

export default LeaderboardCSVUpload;
